import { motion } from "framer-motion";
import { Database, BarChart2, Brain, Workflow, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { fadeInUp, staggerContainer } from "@/lib/constants";

const servicios = [
  {
    icon: <Database className="w-7 h-7" />,
    title: "Integración y modelado de datos",
    description: "Conecto tu ERP, CRM, hojas de Excel, APIs y bases de datos en un único modelo coherente. SQL, ETL y Power Query para que el dato llegue limpio y listo para analizar.",
    items: [
      "Pipelines ETL entre ERP, bases de datos (PostgreSQL, MySQL, SQL Server) y APIs",
      "Limpieza, normalización y deduplicación a nivel de fuente",
      "Modelo de datos relacional o en estrella optimizado para reporting",
      "Capa semántica con DAX para que el negocio consulte sin tocar SQL"
    ]
  },
  {
    icon: <BarChart2 className="w-7 h-7" />,
    title: "Dashboards y KPIs en Power BI",
    description: "Cuadros de mando construidos desde el modelo, con KPIs reales del negocio y diseño orientado a lectura ejecutiva en 30 segundos.",
    items: [
      "Margen real por producto, cliente, canal y zona",
      "Control de stock: rotación, días de cobertura y stock muerto",
      "KPIs de operaciones: rendimiento por línea, turno y referencia",
      "Alertas automáticas sobre desviaciones y umbrales críticos"
    ]
  },
  {
    icon: <Brain className="w-7 h-7" />,
    title: "Analítica avanzada y forecasting",
    description: "Cuando los dashboards no bastan: modelos predictivos en Python para anticipar demanda, fugas de clientes y comportamiento operativo.",
    items: [
      "Forecast de ventas y demanda por SKU, temporada y zona",
      "Detección de clientes en riesgo de churn",
      "Análisis de cohortes, segmentación y clustering",
      "Modelos de planificación de compras y stock óptimo"
    ]
  },
  {
    icon: <Workflow className="w-7 h-7" />,
    title: "Automatización y reporting",
    description: "El reporting que hoy alguien hace a mano cada lunes pasa a ejecutarse solo. Menos horas de hoja de cálculo, más decisiones.",
    items: [
      "Reportes semanales y mensuales con refresco automático",
      "Scripts Python para tareas repetitivas y consolidación de archivos",
      "Integración de datos entre sistemas sin intervención manual",
      "Distribución programada por email, Teams o Power BI Service"
    ]
  }
];

export const ServiciosSection = () => {
  return (
    <section id="servicios" className="relative section-padding" data-testid="servicios-section">
      <div className="spotlight-1 top-0 right-0" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="text-indigo-400 font-medium text-sm uppercase tracking-wider">
              Servicios
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4">
              Cuatro capas, <span className="text-gradient">un mismo objetivo</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mt-4 text-base">
              Desde la integración de tus fuentes hasta el modelo predictivo. Cada empresa entra en la capa que necesita.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servicios.map((servicio, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`servicio-card-${index}`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="w-14 h-14 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-5">
                      {servicio.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{servicio.title}</h3>
                    <p className="text-slate-400 text-sm mb-5">{servicio.description}</p>
                    <ul className="space-y-2">
                      {servicio.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <ChevronRight className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
